/* eslint-disable react/react-in-jsx-scope */
import Breadcrumbs from "components/Common/Breadcrumb"
import { extractBuildIdFromWebLink, getTotalFetchedFromBuildLogs, listPipelineRuns, runPipeline } from "helpers/azdo_helper"
import { listBots, listTaskCounts, listTasks, mapTaskStatus } from "helpers/raspagem_helper"
import { useEffect, useMemo, useState } from "react"
import { Button, Card, CardBody, Col, Container, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap"
import toastr from "toastr"

// Lista fixa de robôs exibidos nos cards de contagem
const ROBOT_NAMES = [
  "Banco do Brasil - Depósito Judicial",
  "Banco do Brasil - Depósito Judicial TED",
  "Banco do Brasil - Dep. em Continuação",
  "CEF - Levantamento Dep. Judicial",
  "CEF - Guia Depósito Judicial",
  "CEF - Conta CNJ",
  "TJSP - Dados de Processos",
]

const PainelRaspagemPage = () => {
  document.title = "Painel Raspagem"

  const [isRunning, setIsRunning] = useState(false)
  const [stats, setStats] = useState({
    lastRunAt: null,
    totalFetched: null,
    lastFailedAt: null,
  })
  const [manualRunHistory, setManualRunHistory] = useState([])
  const [runs, setRuns] = useState([])
  const [isLoadingRuns, setIsLoadingRuns] = useState(false)
  const [isLoadingTasks, setIsLoadingTasks] = useState(false)
  const [tasksByRobot, setTasksByRobot] = useState({})
  const [tasksRunRef, setTasksRunRef] = useState({ runId: null, dt_min: null, dt_max: null })
  const [isLoadingCounts, setIsLoadingCounts] = useState(false)
  const [taskCounts, setTaskCounts] = useState([])
  const [countsByName, setCountsByName] = useState({})
  const [isLoadingBots, setIsLoadingBots] = useState(false)
  const [bots, setBots] = useState([])
  const [isBotsModalOpen, setIsBotsModalOpen] = useState(false)
  const [botsModalRunId, setBotsModalRunId] = useState(null)
  const [isLoadingBotsModal, setIsLoadingBotsModal] = useState(false)
  const [botsModalItems, setBotsModalItems] = useState([])

  const normalizeKey = value => {
    if (!value) return ""
    let s = String(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // remove acentos
    // normaliza hífens/traços e remove caracteres não alfanuméricos
    s = s.replace(/[\u2012\u2013\u2014\u2015\-]+/g, "-") // todos tipos de traço → '-'
    s = s.replace(/[^A-Za-z0-9\-\s\.]/g, " ") // remove símbolos estranhos
    s = s.replace(/\s+/g, " ") // colapsa espaços
    s = s.replace(/\s*\-\s*/g, "-") // espaços ao redor de '-'
    return s.trim().toUpperCase()
  }

  const toCountsMap = list => {
    const map = {}
    ;(Array.isArray(list) ? list : []).forEach(item => {
      const key = normalizeKey(item?.Robo)
      if (!key) return
      map[key] = typeof item?.Tarefas === "number" ? item.Tarefas : null
    })
    return map
  }

  const formatDateTime = value => {
    if (!value) return "-"
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return "-"
    const day = `0${date.getDate()}`.slice(-2)
    const month = `0${date.getMonth() + 1}`.slice(-2)
    const year = date.getFullYear()
    const hours = `0${date.getHours()}`.slice(-2)
    const minutes = `0${date.getMinutes()}`.slice(-2)
    return `${day}/${month}/${year} ${hours}:${minutes}`
  }

  const totalFetchedFormatted = useMemo(
    () => stats.totalFetched?.toLocaleString("pt-BR") ?? "0",
    [stats.totalFetched]
  )

  async function loadRuns({ signal } = {}) {
    setIsLoadingRuns(true)
    try {
      const data = await listPipelineRuns({
        organization: "blopsbr",
        project: "Robos",
        pipelineId: 30,
        top: 20,
        signal,
      })
      const items = Array.isArray(data?.value) ? data.value : []
      setRuns(items)
      const lastCompleted = items.find(item => item?.state === "completed")
      const lastSucceeded = items.find(item => item?.state === "completed" && item?.result === "succeeded")
      const lastFailed = items.find(item => item?.state === "completed" && item?.result === "failed")

      setStats(prev => ({
        ...prev,
        lastRunAt: lastCompleted?.finishedDate || lastCompleted?.createdDate || null,
        lastFailedAt: lastFailed?.finishedDate || null,
      }))

      if (lastSucceeded?._links?.web?.href) {
        const buildId = extractBuildIdFromWebLink(lastSucceeded._links.web.href)
        if (buildId) {
          try {
            const total = await getTotalFetchedFromBuildLogs({
              organization: "blopsbr",
              project: "Robos",
              buildId,
              signal,
            })
            if (typeof total === "number") {
              setStats(prev => ({ ...prev, totalFetched: total }))
            }
          } catch (err) {
            console.error(err)
          }
        }
      }

      // Carrega tarefas da raspagem para o run mais recente (primeiro da lista)
      const selectedRun = items[0]
      if (selectedRun?.createdDate) {
        setIsLoadingTasks(true)
        setIsLoadingCounts(true)
        try {
          const dtMin = selectedRun.createdDate
          const dtMax = selectedRun.finishedDate || null
          const [dataTasks, dataCounts] = await Promise.all([
            listTasks({ dt_min: dtMin, dt_max: dtMax, signal }),
            listTaskCounts({ dt_min: dtMin, dt_max: dtMax, signal }),
          ])
          // Espera-se um objeto onde as chaves são nomes dos robôs
          setTasksByRobot(dataTasks && typeof dataTasks === "object" ? dataTasks : {})
          setTaskCounts(Array.isArray(dataCounts) ? dataCounts : [])
          setCountsByName(toCountsMap(dataCounts))
          setTasksRunRef({ runId: selectedRun.id, dt_min: dtMin, dt_max: dtMax })
          // Buscar status dos bots referente ao run selecionado
          try {
            setIsLoadingBots(true)
            const dataBots = await listBots({ run_id: selectedRun.id, signal })
            setBots(Array.isArray(dataBots) ? dataBots : [])
          } catch (e) {
            console.error(e)
          } finally {
            setIsLoadingBots(false)
          }
        } catch (err) {
          console.error(err)
          toastr.error(err?.message || "Falha ao buscar tarefas da raspagem")
          setTasksByRobot({})
          setTaskCounts([])
          setCountsByName({})
          setTasksRunRef({ runId: null, dt_min: null, dt_max: null })
        } finally {
          setIsLoadingTasks(false)
          setIsLoadingCounts(false)
        }
      } else {
        setTasksByRobot({})
        setTaskCounts([])
        setCountsByName({})
        setTasksRunRef({ runId: null, dt_min: null, dt_max: null })
      }
    } catch (error) {
      console.error(error)
      toastr.error(error?.message || "Falha ao buscar execuções do pipeline no Azure DevOps")
    } finally {
      setIsLoadingRuns(false)
    }
  }

  useEffect(() => {
    const abortController = new AbortController()
    loadRuns({ signal: abortController.signal })
    // Carregar contagens sem depender das runs
    ;(async () => {
      try {
        setIsLoadingCounts(true)
        const dataCounts = await listTaskCounts({ signal: abortController.signal })
        setTaskCounts(Array.isArray(dataCounts) ? dataCounts : [])
        setCountsByName(toCountsMap(dataCounts))
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoadingCounts(false)
      }
    })()
    return () => abortController.abort()
  }, [])

  const handleRunRobot = async () => {
    if (isRunning) return
    setIsRunning(true)
    try {
      await runPipeline({
        organization: "blopsbr",
        project: "Robos",
        pipelineId: 30,
      })
      toastr.success("Robô disparado com sucesso")
      await loadRuns()
      // Recarregar contagens após disparo
      try {
        const dataCounts = await listTaskCounts()
        setTaskCounts(Array.isArray(dataCounts) ? dataCounts : [])
        setCountsByName(toCountsMap(dataCounts))
      } catch (e) {
        console.error(e)
      }
    } catch (error) {
      console.error(error)
      toastr.error(error?.message || "Falha ao executar o robô")
    } finally {
      setIsRunning(false)
    }
  }

  const openBotsModal = async runId => {
    if (!runId) return
    setBotsModalRunId(runId)
    setIsBotsModalOpen(true)
    setIsLoadingBotsModal(true)
    try {
      const dataBots = await listBots({ run_id: runId })
      setBotsModalItems(Array.isArray(dataBots) ? dataBots : [])
    } catch (error) {
      console.error(error)
      toastr.error(error?.message || "Falha ao buscar status dos robôs")
      setBotsModalItems([])
    } finally {
      setIsLoadingBotsModal(false)
    }
  }

  const closeBotsModal = () => {
    setIsBotsModalOpen(false)
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Robô" breadcrumbItem="Painel Raspagem" />

        <Row className="mb-3">
          <Col className="d-flex justify-content-end">
            <Button color="primary" onClick={handleRunRobot} disabled={isRunning}>
              {isRunning ? (
                <span className="d-inline-flex align-items-center">
                  <Spinner size="sm" className="me-2" /> Rodando...
                </span>
              ) : (
                "Rodar Robô manualmente"
              )}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xl="4" md="6" className="mb-3">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm me-3">
                    <span className="avatar-title rounded-circle bg-primary bg-soft text-primary fs-4">
                      <i className="bx bx-time" />
                    </span>
                  </div>
                  <div>
                    <p className="text-muted mb-1">Última execução</p>
                    <h5 className="mb-0">{formatDateTime(stats.lastRunAt)}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" md="6" className="mb-3">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm me-3">
                    <span className="avatar-title rounded-circle bg-danger bg-soft text-danger fs-4">
                      <i className="bx bx-error-circle" />
                    </span>
                  </div>
                  <div>
                    <p className="text-muted mb-1">Última falha</p>
                    <h5 className="mb-0">{formatDateTime(stats.lastFailedAt)}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {ROBOT_NAMES.map(robotName => {
            const value = countsByName[normalizeKey(robotName)]
            return (
            <Col key={robotName} xl="4" md="6" className="mb-3">
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <span className="avatar-title rounded-circle bg-warning bg-soft text-warning fs-4">
                        <i className="bx bx-list-ol" />
                      </span>
                    </div>
                    <div>
                      <p className="text-muted mb-1">{robotName}</p>
                      <h5 className="mb-0">{value == null ? "-" : Number(value).toLocaleString("pt-BR")}</h5>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )})}
        </Row>

        <Row>
          <Col xl="12" className="mb-3">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Execuções do Pipeline (Azure DevOps)</h5>
                </div>
                <div className="bg-light rounded p-3" style={{ maxHeight: 360, overflowY: "auto" }}>
                  {isLoadingRuns ? (
                    <div className="d-flex align-items-center text-muted">
                      <Spinner size="sm" className="me-2" /> Carregando execuções...
                    </div>
                  ) : runs?.length === 0 ? (
                    <div className="text-muted">Nenhuma execução encontrada.</div>
                  ) : (
                    <ul className="list-unstyled mb-0">
                      {runs.map(run => {
                        const created = run?.createdDate
                        const finished = run?.finishedDate
                        const webUrl = run?._links?.web?.href
                        const isCompleted = run?.state === "completed"
                        const succeeded = isCompleted && run?.result === "succeeded"
                        const failed = isCompleted && run?.result === "failed"
                        const color = succeeded ? "text-success" : failed ? "text-danger" : run?.state === "inProgress" ? "text-primary" : "text-muted"
                        return (
                          <li key={run?.id} className="mb-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="me-3">
                                <div className="fw-semibold">#{run?.id} • {run?.name || "run"}</div>
                                <div className="small text-muted">
                                  Criado: {formatDateTime(created)}{finished ? ` • Finalizado: ${formatDateTime(finished)}` : ""}
                                </div>
                              </div>
                              <div className="d-flex align-items-center">
                                <span className={`me-3 fw-semibold ${color}`}>
                                  {isCompleted ? (succeeded ? "Sucesso" : failed ? "Falha" : (run?.result || "Finalizado")) : (run?.state === "inProgress" ? "Em progresso" : (run?.state || "-"))}
                                </span>
                                <button type="button" onClick={() => openBotsModal(run?.id)} className="btn btn-sm btn-outline-secondary">
                                  Abrir
                                </button>
                              </div>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* seção de contagem removida pois cards agora ficam junto aos cards superiores */}

        <Row>
          <Col xl="12" className="mb-3">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Logging das Tarefas (Raspagem)</h5>
                  <div className="small text-muted">
                    {tasksRunRef?.runId ? (
                      <>Run #{tasksRunRef.runId} • Janela: {formatDateTime(tasksRunRef.dt_min)}{tasksRunRef.dt_max ? ` — ${formatDateTime(tasksRunRef.dt_max)}` : " (em execução)"}</>
                    ) : (
                      <>Sem run selecionado</>
                    )}
                  </div>
                </div>
                <div className="bg-light rounded p-3" style={{ maxHeight: 360, overflowY: "auto" }}>
                  {isLoadingTasks ? (
                    <div className="d-flex align-items-center text-muted">
                      <Spinner size="sm" className="me-2" /> Carregando tarefas...
                    </div>
                  ) : !tasksByRobot || Object.keys(tasksByRobot).length === 0 ? (
                    <div className="text-muted">Nenhuma tarefa encontrada para o intervalo selecionado.</div>
                  ) : (
                    <div>
                      {Object.entries(tasksByRobot).map(([robotName, tasks]) => (
                        <div key={robotName} className="mb-3">
                          <div className="fw-semibold mb-2">{robotName}</div>
                          <ul className="list-unstyled mb-0">
                            {(Array.isArray(tasks) ? tasks : []).map(task => {
                              const statusInfo = mapTaskStatus(task?.statusTarefaID, task?.Conteudo)
                              return (
                                <li key={task?.ID} className="mb-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="me-3">
                                      <div className="fw-semibold">#{task?.ID}</div>
                                      <div className="small text-muted">
                                        Início: {formatDateTime(task?.dtTarefaInicio)}{task?.dtTarefaFim ? ` • Fim: ${formatDateTime(task?.dtTarefaFim)}` : ""}
                                      </div>
                                      {task?.dsMensagem ? (
                                        <div className="small text-muted">{task.dsMensagem}</div>
                                      ) : null}
                                      {task?.Conteudo ? (
                                        <div className="small">
                                          <a href={task.Conteudo} target="_blank" rel="noreferrer">Abrir Conteúdo</a>
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className={`fw-semibold ${statusInfo.colorClass}`}>{statusInfo.label}</div>
                                  </div>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal isOpen={isBotsModalOpen} toggle={closeBotsModal} size="lg">
        <ModalHeader toggle={closeBotsModal}>Status dos Robôs • Run #{botsModalRunId || "-"}</ModalHeader>
        <ModalBody>
          <div className="bg-light rounded p-3" style={{ maxHeight: 480, overflowY: "auto" }}>
            {isLoadingBotsModal ? (
              <div className="d-flex align-items-center text-muted">
                <Spinner size="sm" className="me-2" /> Carregando status dos robôs...
              </div>
            ) : botsModalItems.length === 0 ? (
              <div className="text-muted">Nenhuma informação de robôs para este run.</div>
            ) : (
              <ul className="list-unstyled mb-0">
                {botsModalItems.map(b => {
                  const status = (b?.status || "").toLowerCase()
                  const color = status.includes("aguard") ? "text-muted" : status.includes("exec") ? "text-primary" : status.includes("falha") || status.includes("erro") ? "text-danger" : status.includes("sucesso") ? "text-success" : "text-muted"
                  return (
                    <li key={b?.id} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="me-3">
                          <div className="fw-semibold">{b?.nome || "Robô"}</div>
                          <div className="small text-muted">
                            Início: {b?.dtInicio ? formatDateTime(b.dtInicio) : "-"}{b?.dtFim ? ` • Fim: ${formatDateTime(b.dtFim)}` : ""}
                          </div>
                          {b?.erro ? <div className="small text-danger">{b.erro}</div> : null}
                        </div>
                        <div className={`fw-semibold ${color}`}>{b?.status || "-"}</div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default PainelRaspagemPage
